import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL, getAuthConfig, getUploadConfig, fmtDate } from "./shared";

/*
 * Laboratoire — Aide & Support (cahier des charges labo, section 37), DEDIE.
 *
 * Port de SupportModule (HopitalDashboard.js), repointé sur
 * /api/lab/support/... au lieu de /api/sih/support/... (voir
 * backend/routes/lab/support.py, fork de la partie "côté établissement" de
 * routes/support.py). Le FAQ ci-dessous est repris tel quel : il est déjà
 * rédigé pour un public labo (horaires/capacité, confirmation de RDV,
 * portail patient, messagerie, RGPD).
 */
export default function SupportModule({ can }) {
  const [tickets, setTickets] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState("");
  const [openTicketId, setOpenTicketId] = useState(null);
  const [form, setForm] = useState({ category: "technique", priority: "normale", description: "" });
  const [file, setFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState("");

  const load = () => {
    setLoadingList(true); setListError("");
    axios.get(`${API_URL}/api/lab/support/tickets`, getAuthConfig())
      .then((r) => setTickets(r.data.tickets || []))
      .catch(() => setListError("Impossible de charger vos tickets"))
      .finally(() => setLoadingList(false));
  };
  useEffect(() => { load(); }, []);

  const createTicket = async () => {
    if (!form.description.trim()) { setCreateError("Description requise"); return; }
    setCreating(true); setCreateError("");
    const fd = new FormData();
    fd.append("category", form.category); fd.append("priority", form.priority); fd.append("description", form.description.trim());
    if (file) fd.append("file", file);
    try {
      await axios.post(`${API_URL}/api/lab/support/tickets`, fd, getUploadConfig());
      setForm({ category: "technique", priority: "normale", description: "" }); setFile(null);
      load();
    } catch (e) { setCreateError(e.response?.data?.detail || "Erreur lors de la creation du ticket"); }
    finally { setCreating(false); }
  };

  const viewAttachment = async (t) => {
    try {
      const r = await axios.get(`${API_URL}/api/lab/support/tickets/${t.ticket_id}/attachment`, getAuthConfig());
      window.open(r.data.url, "_blank");
    } catch (e) { /* silencieux : piece jointe optionnelle */ }
  };

  const sendReply = async (ticketId) => {
    if (!replyBody.trim()) return;
    setReplySending(true); setReplyError("");
    try {
      await axios.post(`${API_URL}/api/lab/support/tickets/${ticketId}/reply`, { body: replyBody.trim() }, getAuthConfig());
      setReplyBody(""); load();
    } catch (e) { setReplyError(e.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setReplySending(false); }
  };

  const CATEGORY_LABELS = { technique: "Technique", facturation: "Facturation", compte: "Compte", fonctionnalite: "Suggestion", autre: "Autre" };
  const PRIORITY_LABELS = { basse: "Basse", normale: "Normale", haute: "Haute", urgente: "Urgente" };
  const STATUS_COLORS = { ouvert: ["#FEF3C7", "#92400E"], en_cours: ["#DBEAFE", "#1E40AF"], resolu: ["#DCFCE7", "#166534"], ferme: ["#F1F5F9", "#64748B"] };
  const STATUS_LABELS = { ouvert: "Ouvert", en_cours: "En cours", resolu: "Resolu", ferme: "Ferme" };
  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  const FAQ = [
    ["Comment configurer les horaires et la capacite de mon laboratoire ?", "Dans Agenda, bouton \"Horaires\" : definissez vos plages d'ouverture (avec pauses), la capacite simultanee et les fermetures exceptionnelles."],
    ["Comment activer la confirmation obligatoire des rendez-vous ?", "Dans Agenda > Horaires, activez \"Confirmation obligatoire\" et choisissez le delai avant le rendez-vous. Le patient confirme depuis son portail ; le personnel est alerte si le delai est depasse, mais le rendez-vous n'est jamais annule automatiquement."],
    ["Comment un patient accede-t-il a son portail ?", "Un lien securise, sans mot de passe, est envoye automatiquement par email lors de la creation du rendez-vous ou du rappel."],
    ["Comment repondre a un message envoye par un patient ?", "Ouvrez la fiche du patient concerne, onglet Messages : vous pouvez y lire et repondre, avec piece jointe si besoin."],
    ["Comment exporter ou anonymiser les donnees d'un patient (RGPD) ?", "Dans la fiche patient, onglet RGPD : \"Exporter\" telecharge toutes les donnees du patient ; \"Anonymiser\" efface irreversiblement son identite (reserve au gerant de l'etablissement)."],
    ["Que faire si une fonctionnalite ne marche pas comme prevu ?", "Ouvrez un ticket ci-dessous en decrivant le probleme le plus precisement possible (categorie \"Technique\"), avec une capture d'ecran en piece jointe si possible."],
  ];

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Aide & Support</h1>
      <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4, marginBottom: 20 }}>Centre d'aide et tickets adresses a l'equipe Tabbibak (section 37)</div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>Questions frequentes</div>
        {FAQ.map(([q, a], i) => (
          <div key={i} style={{ marginBottom: i < FAQ.length - 1 ? 14 : 0, paddingBottom: i < FAQ.length - 1 ? 14 : 0, borderBottom: i < FAQ.length - 1 ? "1px solid #F1F5F9" : "none" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A", marginBottom: 4 }}>{q}</div>
            <div style={{ fontSize: 13, color: "#64748B", lineHeight: 1.5 }}>{a}</div>
          </div>
        ))}
      </div>

      {can("support:write") && (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18, marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>Ouvrir un ticket</div>
          {createError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12 }}>{createError}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div><label style={lab}>Categorie</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={iS}>
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
            <div><label style={lab}>Priorite</label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} style={iS}>
              {Object.entries(PRIORITY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          </div>
          <label style={lab}>Description</label>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Decrivez votre probleme ou votre demande..." style={{ ...iS, minHeight: 80, resize: "vertical" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <label style={{ fontSize: 12, color: "#185FA5", cursor: "pointer" }}>
              {file ? `📎 ${file.name}` : "+ Piece jointe (JPEG, PNG, PDF)"}
              <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ display: "none" }} />
            </label>
            <button onClick={createTicket} disabled={creating || !form.description.trim()} style={{ padding: "8px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (creating || !form.description.trim()) ? 0.6 : 1 }}>
              {creating ? "Envoi..." : "Envoyer le ticket"}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>Mes tickets</div>
        {loadingList && <div style={{ fontSize: 13, color: "#94A3B8" }}>Chargement...</div>}
        {listError && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>{listError}</div>}
        {!loadingList && tickets && tickets.length === 0 && <div style={{ fontSize: 13, color: "#94A3B8" }}>Aucun ticket.</div>}
        {!loadingList && tickets && tickets.map((t) => {
          const sc = STATUS_COLORS[t.status] || ["#F1F5F9", "#64748B"];
          const open = openTicketId === t.ticket_id;
          return (
            <div key={t.ticket_id} style={{ borderTop: "1px solid #F1F5F9", padding: "12px 0" }}>
              <div onClick={() => {
                // Correctif L-SUPPORT-DRAFT : replyBody/replyError etaient
                // partages entre TOUS les tickets sans jamais etre
                // reinitialises en changeant de ticket ouvert — un brouillon
                // tape pour un ticket pouvait partir sur un autre ticket
                // (donc potentiellement un autre patient) si on changeait
                // de ticket avant d'envoyer.
                setOpenTicketId(open ? null : t.ticket_id);
                setReplyBody("");
                setReplyError("");
              }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                <div>
                  <div style={{ fontWeight: 500, color: "#0F172A" }}>{CATEGORY_LABELS[t.category] || t.category} · {t.description.slice(0, 70)}{t.description.length > 70 ? "…" : ""}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>{fmtDate(t.created_at)} · {PRIORITY_LABELS[t.priority] || t.priority}</div>
                </div>
                <span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1], whiteSpace: "nowrap" }}>{STATUS_LABELS[t.status] || t.status}</span>
              </div>
              {open && (
                <div style={{ marginTop: 10, paddingLeft: 4 }}>
                  <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", marginBottom: 8 }}>{t.description}</div>
                  {t.attachment && <button onClick={() => viewAttachment(t)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8, display: "block" }}>📎 {t.attachment.filename}</button>}
                  {(t.replies || []).map((r, i) => (
                    <div key={i} style={{ background: r.from === "admin" ? "#E6F1FB" : "#F8FAFC", borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: r.from === "admin" ? "#185FA5" : "#334155" }}>{r.author} · {fmtDate(r.created_at)}</div>
                      <div style={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap", marginTop: 2 }}>{r.body}</div>
                    </div>
                  ))}
                  {can("support:write") && t.status !== "ferme" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <input value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder="Repondre..." style={{ ...iS, flex: 1 }} />
                      <button onClick={() => sendReply(t.ticket_id)} disabled={replySending || !replyBody.trim()} style={{ padding: "8px 14px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, cursor: "pointer", opacity: (replySending || !replyBody.trim()) ? 0.6 : 1 }}>Envoyer</button>
                    </div>
                  )}
                  {replyError && <div style={{ fontSize: 12, color: "#A32D2D", marginTop: 6 }}>{replyError}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
